/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {PackageCreditItemType} from "types/enums/PackageCreditItemType";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT} from "helpers/config";
import {RouteComponentProps, withRouter} from "react-router";
import useBookingPackagesView from "pages/Front/Booking/components/BookingPackageNew/hooks/useBookingPackagesView";
import BookingPackagesViewSkeleton
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesViewSkeleton";
import BookingPackagesViewFooter
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesViewFooter";
import BookingPackagesCreditsEventsDriverRow
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesCreditsEventsDriverRow";
import CreditsInfo from "pages/Front/Booking/Credits/components/CreditsInfo";
import BookingSelectedFilterHeader from "pages/Front/Booking/components/BookingSelectedFilterHeader";

export interface BookingPackagesEventsViewParams {}

interface BookingPackagesEventsViewProps extends RouteComponentProps<BookingPackagesEventsViewParams> {}

const BookingPackagesEventsView = (props: BookingPackagesEventsViewProps) => {
    const {t} = useTranslation();

    const {
        availablePackageItems,
        availableCreditItems,
        selectedPackageItems,
        filter,
        selectedTimeslot,
        personsCount,
        allFilled,
        handleRouteNavigation,
        onNextStepHandler,
        handleChange,
        setPackageCreditItemsHandler,
    } = useBookingPackagesView(props.history, true);

    useEffect(() => {
        if (availableCreditItems.length === 0) {
            return;
        }
        // Pre-select the recommended standard credit pack for everyone (per-package person count model).
        const recommended = availableCreditItems.find(
            item =>
                item.article?.creditPackage?.category === PackageCreditItemType.Standard &&
                item.article?.creditPackage?.amount_total === DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT
        );
        if (!recommended || personsCount === 0) {
            return;
        }
        console.log("doing loading reset of credit items");
        setPackageCreditItemsHandler([
            {
                personIndex: null, //do not sent personIndex on event (non specified index) credits
                id: recommended.id,
                person_type: PackageItemPersonType.Adult,
                quantity: personsCount,
            },
        ]);
    }, [availableCreditItems.length]);

    if (!availablePackageItems?.packageItems || !selectedPackageItems) {
        return <BookingPackagesViewSkeleton className={"BookingPackagesEventsView"} />;
    }

    return (
        <div className={"BookingPackagesEventsView w-full"}>
            <div className={"BookingPackagesEventsViewWrapper border rounded-xl bg-white shadow w-full"}>
                <div
                    className={
                        "BookingPackagesEventsViewHeader grid grid-cols-1 xs:grid-cols-2 lg:flex lg:flex-row items-center rounded-t-xl w-full bg-[#F5F5F566]/40 border-b border-[#D9D9D9] px-4 py-4 lg:px-3 xl:px-4 lg:py-3 xl:py-4 gap-y-2 gap-x-4 xl:gap-8"
                    }
                >
                    <BookingSelectedFilterHeader filter={filter} selectedTimeslot={selectedTimeslot} />
                </div>

                <div className={"BookingPackagesEventsViewContent flex flex-col px-2 lg:px-4 xl:px-6 py-4 lg:py-6 w-full"}>
                    {selectedPackageItems.length > 0 && <BookingPackagesCreditsEventsDriverRow category={filter.category} />}
                </div>

                <CreditsInfo />

                <BookingPackagesViewFooter
                    className={"BookingPackagesEventsViewFooter"}
                    discountImageAlt={"event tickets discount"}
                    onBack={handleRouteNavigation}
                    onContinue={onNextStepHandler}
                    disabled={!allFilled}
                />
            </div>
        </div>
    );
};

export default withRouter(BookingPackagesEventsView);
